import { OrderV2 } from '@kotipizzagroup/kotipizza-orders-api-client';
import { DELIVERY_TYPES } from '../constants';
import { OrderRow, sortOrdersByPlaced } from './orderRowFields';
import { ORDER_STATUS_RANK } from './orderStatus';

export type OrderSortKey = 'newest' | 'oldest' | 'status';

/**
 * The single narrowing the list is under. One key covers both the preorder cut and the order-type
 * cuts because they share a control: the choices read as one list of "which orders am I looking at",
 * and picking one replaces the last rather than stacking with it.
 */
export type OrderFilterKey = 'all' | 'preorders' | 'pickup' | 'delivery' | 'eatin';

export const DEFAULT_ORDER_FILTER: OrderFilterKey = 'all';

export const DEFAULT_SORT_KEY: OrderSortKey = 'newest';

/** Every restaurant the results happen to hold — the filter doing nothing. */
export const ALL_SHOPS = 'all' as const;

declare const shopExternalIdBrand: unique symbol;

/**
 * A restaurant's external id, marked as one.
 *
 * Branded so it cannot be confused with the `ALL_SHOPS` sentinel it shares a value space with: both
 * are strings, and left as plain strings the compiler would let any id be written where the sentinel
 * was meant and the other way round. The brand exists only in the types — at runtime this is the id.
 */
export type ShopExternalId = string & { readonly [shopExternalIdBrand]: true };

/**
 * Which restaurant's orders the list shows: `ALL_SHOPS`, or one restaurant's external id.
 *
 * A view preference, not an access rule. The orders API has already decided which restaurants this
 * user may see at all; this only narrows that set to one of them for the sake of reading it.
 */
export type ShopFilterKey = typeof ALL_SHOPS | ShopExternalId;

/**
 * The one crossing from an untyped id into a filter key — a DTO field, or the string a pill's menu
 * hands back. Everything downstream of it is typed, so the cast lives here and nowhere else.
 */
export const asShopFilterKey = (id: string): ShopFilterKey => id as ShopFilterKey;

// Label keys stay literal strings so i18next-scanner can extract them. `startsGroup` draws a divider
// above the option, separating the preorder cut from the order types.
export const ORDER_FILTER_PRESETS: { value: OrderFilterKey; labelKey: string; startsGroup?: boolean }[] = [
  { value: 'all', labelKey: 'orderList.allOrders' },
  { value: 'preorders', labelKey: 'orderList.preordersOnly' },
  { value: 'pickup', labelKey: 'singleRestaurant.deliveryType.pickup', startsGroup: true },
  { value: 'delivery', labelKey: 'singleRestaurant.deliveryType.delivery' },
  { value: 'eatin', labelKey: 'singleRestaurant.deliveryType.eatin' },
];

export const SORT_PRESETS: { value: OrderSortKey; labelKey: string }[] = [
  { value: 'newest', labelKey: 'orderList.sortNewest' },
  { value: 'oldest', labelKey: 'orderList.sortOldest' },
  { value: 'status', labelKey: 'global.status' },
];

// Hot-stop orders have no option of their own, so they are only ever reached through 'all'.
const DELIVERY_TYPE_BY_FILTER: Partial<Record<OrderFilterKey, number>> = {
  pickup: DELIVERY_TYPES.pickup,
  delivery: DELIVERY_TYPES.delivery,
  eatin: DELIVERY_TYPES.eatin,
};

export const filterOrders = (rows: OrderRow[], key: OrderFilterKey): OrderRow[] => {
  if (key === 'preorders') {
    return rows.filter((row) => !!row.preOrderTime);
  }

  const deliveryType = DELIVERY_TYPE_BY_FILTER[key];

  return deliveryType === undefined ? rows : rows.filter((row) => row.deliveryType === deliveryType);
};

/**
 * The distinct restaurants a set of orders touches, both ends of any redirect included.
 *
 * The one reading of "which restaurants are in these results", shared by the filter pill that offers
 * them and the note that counts them — so the menu cannot list two restaurants under a sentence
 * calling them one.
 */
export const shopIdsInOrders = (
  orders?: Pick<OrderV2, 'ShopExternalId' | 'redirectedToShopExternalId'>[]
): string[] => {
  const present = new Set<string>();

  orders?.forEach((order) => {
    if (order.ShopExternalId) present.add(order.ShopExternalId);
    if (order.redirectedToShopExternalId) present.add(order.redirectedToShopExternalId);
  });

  return [...present];
};

/**
 * Narrows the list to one restaurant.
 *
 * An order redirected to the chosen restaurant counts as that restaurant's: it is the one that made
 * it. The same reading the rest of the orders code takes of `redirectedToShopExternalId`.
 */
export const filterOrdersByShop = (rows: OrderRow[], key: ShopFilterKey): OrderRow[] => {
  if (key === ALL_SHOPS) {
    return rows;
  }

  return rows.filter((row) => row.ShopExternalId === key || row.redirectedToShopExternalId === key);
};

/**
 * Sorting by status leans on `Array.prototype.sort` being stable: the rows are put in time order first,
 * so orders sharing a status still read newest-first inside their group.
 */
export const sortOrders = (rows: OrderRow[], key: OrderSortKey): OrderRow[] => {
  if (key === 'oldest') {
    return sortOrdersByPlaced(rows, 'asc');
  }

  if (key === 'status') {
    return sortOrdersByPlaced(rows, 'desc').sort(
      (a, b) => ORDER_STATUS_RANK[a.orderStatusString] - ORDER_STATUS_RANK[b.orderStatusString]
    );
  }

  return sortOrdersByPlaced(rows, 'desc');
};
